import { config } from './config';

interface AnimationOptions {
  threshold?: number;
  rootMargin?: string;
  animatedClass?: string;
  elements?: string;
}

/**
 * Initialize scroll-based fade animations using IntersectionObserver
 */
export function initFadeAnimations(options: AnimationOptions = {}): void {
  const {
    threshold = config.animation.fadeThreshold,
    rootMargin = config.animation.fadeRootMargin,
    animatedClass = 'animate-in',
    elements = '.fade-in, .fade-in-up, .fade-in-left, .fade-in-right, .service-card, .feature-card, .faq-item, .testimonial-card'
  } = options;

  const animatedElements = document.querySelectorAll(elements);

  if (animatedElements.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(animatedClass);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold, rootMargin }
  );

  animatedElements.forEach((el) => observer.observe(el));

  console.log(`Scroll animations initialized for ${animatedElements.length} elements`);
}

/**
 * Initialize header scroll effect
 */
export function initHeaderScroll(options: {
  threshold?: number;
  scrollClass?: string;
} = {}): void {
  const {
    threshold = config.animation.scrollThreshold,
    scrollClass = config.animation.headerScrollClass
  } = options;

  const header = document.querySelector('.site-header') as HTMLElement;
  if (!header) return;

  const checkScroll = (): void => {
    if (window.scrollY > threshold) {
      header.classList.add(scrollClass);
    } else {
      header.classList.remove(scrollClass);
    }
  };

  // Initial check
  checkScroll();

  // Listen for scroll with requestAnimationFrame for performance
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        checkScroll();
        ticking = false;
      });
      ticking = true;
    }
  });
}

/**
 * Initialize smooth scroll for anchor links
 */
export function initSmoothScroll(): void {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', function(this: HTMLAnchorElement, e) {
      const href = this.getAttribute('href');
      if (!href || href === '#') return;

      e.preventDefault();
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });
}

/**
 * Initialize all scroll-related animations
 */
export function initScrollAnimations(): void {
  initFadeAnimations();
  initHeaderScroll();
  initSmoothScroll();
}
